import { createClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = 'https://yumlimcvuwxjxqrypqwy.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl1bWxpbWN2dXd4anhxcnlwcXd5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDUyMDk5NTcsImV4cCI6MjA2MDc4NTk1N30.0r_eUU8cWVevUfq7bW2pB4E-0pfPr6g909Aj6wqQy2U';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Transaction-related functions
export const getTransactions = async (walletAddress) => {
  const { data, error } = await supabase
    .from('transactions')
    .select('*')
    .eq('from_address', walletAddress)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching transactions:', error);
    return [];
  }

  return data || [];
};

export const getReceivedTransactions = async (walletAddress) => {
  const { data, error } = await supabase
    .from('transactions')
    .select('*')
    .eq('to_address', walletAddress)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching received transactions:', error);
    return [];
  }

  return data || [];
};

export const createTransaction = async (transaction) => {
  const { data, error } = await supabase
    .from('transactions')
    .insert([
      {
        from_address: transaction.fromAddress,
        to_address: transaction.toAddress,
        amount: transaction.amount,
        token: transaction.token,
        network: transaction.network,
        transaction_hash: transaction.transactionHash,
        status: transaction.status,
        type: transaction.type,
        gas_fee: transaction.gasFee
      }
    ])
    .select();

  if (error) {
    console.error('Error creating transaction:', error);
    return null;
  }

  return data?.[0] || null;
};

export const updateWalletBalance = async (walletAddress, token, amount, operation = 'subtract') => {
  // First, get the current wallet
  const { data: wallet, error: fetchError } = await supabase
    .from('wallets')
    .select('*')
    .eq('address', walletAddress)
    .eq('token', token)
    .single();

  if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "no rows returned"
    console.error('Error fetching wallet:', fetchError);
    return null;
  }

  let newBalance = 0;

  if (wallet) {
    // Update existing wallet
    newBalance = operation === 'subtract'
      ? parseFloat(wallet.balance) - parseFloat(amount)
      : parseFloat(wallet.balance) + parseFloat(amount);

    const { data, error } = await supabase
      .from('wallets')
      .update({ balance: newBalance })
      .eq('id', wallet.id)
      .select();

    if (error) {
      console.error('Error updating wallet balance:', error);
      return null;
    }

    return data?.[0] || null;
  } else {
    // Create new wallet entry
    newBalance = operation === 'subtract' ? -parseFloat(amount) : parseFloat(amount);

    const { data, error } = await supabase
      .from('wallets')
      .insert([
        {
          address: walletAddress,
          token: token,
          balance: newBalance,
          last_updated: new Date().toISOString()
        }
      ])
      .select();

    if (error) {
      console.error('Error creating wallet:', error);
      return null;
    }

    return data?.[0] || null;
  }
};

export const getWalletBalance = async (walletAddress, token) => {
  const { data, error } = await supabase
    .from('wallets')
    .select('balance')
    .eq('address', walletAddress)
    .eq('token', token)
    .single();

  if (error) {
    if (error.code === 'PGRST116') { // No rows found
      return 0;
    }
    console.error('Error fetching wallet balance:', error);
    return null;
  }

  return data?.balance || 0;
};

export const getNotifications = async (walletAddress, limit = 5) => {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('wallet_address', walletAddress)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching notifications:', error);
    return [];
  }

  return data || [];
};

export const createNotification = async (notification) => {
  const { data, error } = await supabase
    .from('notifications')
    .insert([
      {
        wallet_address: notification.walletAddress,
        message: notification.message,
        type: notification.type,
        is_read: false,
        related_transaction: notification.transactionId
      }
    ])
    .select();

  if (error) {
    console.error('Error creating notification:', error);
    return null;
  }

  return data?.[0] || null;
};

export const markNotificationAsRead = async (notificationId) => {
  const { data, error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId)
    .select();

  if (error) {
    console.error('Error marking notification as read:', error);
    return null;
  }

  return data?.[0] || null;
};
